import React, { useState, useEffect } from 'react';
import { RefreshCw, Instagram, Banknote, Users, HelpCircle, CircleUser as UserCircle, LogOut, PlusCircle, Sun, Moon, ChevronDown, ExternalLink, Gauge, Target, BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  useClick,
  useDismiss,
  useInteractions,
  FloatingFocusManager,
  useRole
} from '@floating-ui/react';
import { languages, translations } from '../../translations';
import { supabase } from '../../lib/supabase';

interface SidebarProps {
  selectedItem: string;
  setSelectedItem: (item: string) => void;
  language: string;
  setLanguage: (lang: string) => void;
  isDarkMode: boolean;
  setIsDarkMode: (isDark: boolean) => void;
  onAddAccount: () => void;
  onLogout: () => void;
  onClose?: () => void;
  isAdmin?: boolean;
  isCloser?: boolean;
}

export function Sidebar({ 
  selectedItem, 
  setSelectedItem, 
  language, 
  setLanguage,
  isDarkMode,
  setIsDarkMode,
  onAddAccount,
  onLogout,
  onClose,
  isAdmin = false,
  isCloser = false
}: SidebarProps) {
  const navigate = useNavigate();
  const [isLanguageOpen, setIsLanguageOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    loadUnreadCount();
  }, []);

  const loadUnreadCount = async () => {
    try {
      const { data: announcements } = await supabase
        .from('announcements')
        .select('id, created_at');

      if (!announcements) return;

      const { data: readAnnouncements } = await supabase
        .from('read_announcements')
        .select('announcement_id');

      const readIds = new Set(readAnnouncements?.map(ra => ra.announcement_id) || []);
      const unreadCount = announcements.filter(a => !readIds.has(a.id)).length;

      setUnreadCount(unreadCount);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const {refs, floatingStyles, context} = useFloating({
    open: isLanguageOpen,
    onOpenChange: setIsLanguageOpen,
    middleware: [offset(4), flip()],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const {getReferenceProps, getFloatingProps} = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const handleNavigation = (route: string, itemKey: string) => {
    setSelectedItem(itemKey);
    navigate(route);
    if (onClose) onClose();
  };

  return (
    <div className={`w-56 h-[100dvh] lg:h-screen border-r flex flex-col bg-opacity-100 ${
      isDarkMode 
        ? 'bg-[#121212] border-[#2b2b2b]' 
        : 'bg-white border-[#e5e7eb]'
    }`}>
      {/* Logo and Title */}
      <div className="p-4 flex items-center gap-3">
        <img 
          src="https://ltyddaykojdpnqqlxhzz.supabase.co/storage/v1/object/public/avatars/logo/webclip.png"
          alt="Amplr AI"
          className="w-8 h-8 rounded-lg"
        />
        <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>
          Amplr AI
        </span>
      </div>

      {/* Separator */}
      <div className="px-4 mb-3">
        <div className={`h-[1px] ${isDarkMode ? 'bg-[#2b2b2b]' : 'bg-[#e5e7eb]'}`}></div>
      </div>

      <div className="flex-1 p-3 overflow-y-auto">
        <nav className="space-y-0.5">
          <button 
            onClick={() => handleNavigation('/', 'instagram')}
            className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
              selectedItem === 'instagram'
                ? isDarkMode
                  ? 'bg-[#1c1c1c] text-gray-200 font-medium'
                  : 'bg-gray-100 text-gray-900 font-medium'
                : isDarkMode
                  ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
            }`}
          >
            <Instagram size={15} strokeWidth={2.3} />
            <span>Instagram</span>
          </button>

          <button 
            onClick={() => isAdmin ? handleNavigation('/tiktok', 'tiktok') : undefined}
            className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
              !isAdmin ? 'opacity-50 cursor-not-allowed' : ''
            } ${
              selectedItem === 'tiktok'
                ? isDarkMode
                  ? 'bg-[#1c1c1c] text-gray-200 font-medium'
                  : 'bg-gray-100 text-gray-900 font-medium'
                : isDarkMode
                  ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
            }`}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="flex-shrink-0">
              <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-.04-.1z"/>
            </svg>
            <span className="flex-1">TikTok</span>
            {!isAdmin && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-500">
                Soon
              </span>
            )}
          </button>

          <button 
            onClick={() => (isAdmin || isCloser) ? handleNavigation('/competitors', 'competitors') : undefined}
            className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
              !(isAdmin || isCloser) ? 'opacity-50 cursor-not-allowed' : ''
            } ${
              selectedItem === 'competitors' 
                ? isDarkMode
                  ? 'bg-[#1c1c1c] text-gray-200 font-medium'
                  : 'bg-gray-100 text-gray-900 font-medium'
                : isDarkMode
                  ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
            }`}
          >
            <Target size={15} strokeWidth={2.3} />
            <span className="flex-1">Competitors</span>
            {!(isAdmin || isCloser) && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-500">
                Soon
              </span>
            )}
          </button>

          <button 
            onClick={() => isAdmin ? handleNavigation('/music-charts', 'music-charts') : undefined}
            className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
              !isAdmin ? 'opacity-50 cursor-not-allowed' : ''
            } ${
              selectedItem === 'music-charts' 
                ? isDarkMode
                  ? 'bg-[#1c1c1c] text-gray-200 font-medium'
                  : 'bg-gray-100 text-gray-900 font-medium'
                : isDarkMode
                  ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
            }`}
          >
            <BarChart3 size={15} strokeWidth={2.3} />
            <span className="flex-1">Music Charts</span>
            {!isAdmin && (
              <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-500">
                Soon
              </span>
            )}
          </button>

        </nav>
      </div>

      {isAdmin && (
        <div className="px-3 mb-3">
          <button 
            onClick={() => handleNavigation('/closing', 'closing')}
            className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] hover:bg-red-500/10 hover:text-red-500 ${
              selectedItem === 'closing' 
                ? 'bg-red-500/10 text-red-500 font-medium'
                : isDarkMode
                  ? 'text-gray-400'
                  : 'text-gray-500'
            }`}
          >
            <Banknote size={15} strokeWidth={2.3} />
            <span>Closing</span>
          </button>
        </div>
      )}

      <div className="p-3 border-t border-gray-200 dark:border-gray-800">
        <button 
          onClick={() => handleNavigation('/dashboard', 'dashboard')}
          className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
            selectedItem === 'dashboard' 
              ? isDarkMode
                ? 'bg-[#1c1c1c] text-gray-200 font-medium'
                : 'bg-gray-100 text-gray-900 font-medium'
              : isDarkMode
                ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
          }`}
        >
          <RefreshCw size={15} strokeWidth={2.3} />
          <span className="flex-1">{t.dashboard}</span>
          {unreadCount > 0 && (
            <span className="bg-orange-500 text-white text-[11px] font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
              {unreadCount}
            </span>
          )}
        </button>

        <div 
          className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] opacity-50 cursor-not-allowed ${
            isDarkMode
              ? 'text-gray-400'
              : 'text-gray-500'
          }`}
        >
          <Users size={15} strokeWidth={2.3} />
          <span className="flex-1">{t.referral}</span>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-500">
            Soon
          </span>
        </div>

        <a 
          href="https://help.amplr.ai/"
          target="_blank"
          rel="noopener noreferrer"
          className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
            isDarkMode
              ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
          }`}
        >
          <HelpCircle size={15} strokeWidth={2.3} />
          <span className="flex-1">{t.help_support}</span>
          <ExternalLink size={12} strokeWidth={2.3} />
        </a>

        <div className="px-3 my-2">
          <div className={`w-1/2 h-[1px] ${isDarkMode ? 'bg-[#2b2b2b]' : 'bg-[#e5e7eb]'}`}></div>
        </div>

        <div ref={refs.setReference} {...getReferenceProps()}>
          <button 
            className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
              isDarkMode
                ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
            }`}
          >
            <span className="text-base">{languages.find(lang => lang.code === language)?.flag}</span>
            <span className="flex-1">{languages.find(lang => lang.code === language)?.name}</span>
            <ChevronDown size={14} strokeWidth={2.3} />
          </button>
        </div>

        {isLanguageOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className={`w-48 rounded-lg border shadow-lg py-1 ${
                isDarkMode 
                  ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                  : 'bg-white border-[#e5e7eb]'
              }`}
            >
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsLanguageOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2.5 transition-colors text-[13px] ${
                    isDarkMode
                      ? 'hover:bg-[#2b2b2b] text-gray-300'
                      : 'hover:bg-gray-50 text-gray-600'
                  } ${language === lang.code ? 'font-medium' : ''}`}
                >
                  <span className="text-base">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </FloatingFocusManager>
        )}

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
            isDarkMode
              ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
          }`}
        >
          {isDarkMode ? (
            <Sun size={15} strokeWidth={2.3} />
          ) : (
            <Moon size={15} strokeWidth={2.3} />
          )}
          <span>{isDarkMode ? t.light_mode : t.dark_mode}</span>
        </button>

        <div className="px-3 my-2">
          <div className={`w-1/2 h-[1px] ${isDarkMode ? 'bg-[#2b2b2b]' : 'bg-[#e5e7eb]'}`}></div>
        </div>

        <button 
          onClick={() => handleNavigation('/account', 'account')}
          className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
            selectedItem === 'account' 
              ? isDarkMode
                ? 'bg-[#1c1c1c] text-gray-200 font-medium'
                : 'bg-gray-100 text-gray-900 font-medium'
              : isDarkMode
                ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
          }`}
        >
          <UserCircle size={15} strokeWidth={2.3} />
          <span>{t.my_account}</span>
        </button>

        <button 
          onClick={() => {
            onLogout();
            if (onClose) onClose();
          }}
          className={`w-full text-left px-3 py-[7px] rounded-lg flex items-center gap-2.5 transition-all duration-200 text-[13px] ${
            isDarkMode
              ? 'text-gray-400 hover:bg-[#1c1c1c] hover:text-gray-300'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-600'
          }`}
        >
          <LogOut size={15} strokeWidth={2.3} />
          <span>{t.logout}</span>
        </button>
      </div>
    </div>
  );
}